import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const FileUploadSection = ({ files, onFilesChange, maxFiles = 5, maxSizePerFile = 10 }) => {
  const [dragActive, setDragActive] = useState(false);
  const [uploadErrors, setUploadErrors] = useState([]);

  const handleDrag = (e) => {
    e?.preventDefault();
    e?.stopPropagation();
    if (e?.type === "dragenter" || e?.type === "dragover") {
      setDragActive(true);
    } else if (e?.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e?.preventDefault();
    e?.stopPropagation();
    setDragActive(false);
    
    if (e?.dataTransfer?.files && e?.dataTransfer?.files?.[0]) {
      handleFiles(e?.dataTransfer?.files);
    }
  };

  const handleChange = (e) => {
    e?.preventDefault();
    if (e?.target?.files && e?.target?.files?.[0]) {
      handleFiles(e?.target?.files);
    }
  };

  const handleFiles = (fileList) => {
    const newFiles = Array.from(fileList);
    const errors = [];
    const validFiles = [];

    // Check file count
    if (files?.length + newFiles?.length > maxFiles) {
      errors?.push(`Maximum ${maxFiles} files allowed`);
      return;
    }

    // Validate each file
    newFiles?.forEach((file, index) => {
      // Check file size (convert MB to bytes)
      if (file?.size > maxSizePerFile * 1024 * 1024) {
        errors?.push(`${file?.name} is too large. Maximum size is ${maxSizePerFile}MB`);
        return;
      }

      // Check file type
      const allowedTypes = [
        'application/pdf',
        'application/msword',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        'application/vnd.ms-excel',
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        'image/jpeg',
        'image/png',
        'image/gif',
        'text/plain'
      ];

      if (!allowedTypes?.includes(file?.type)) {
        errors?.push(`${file?.name} is not a supported file type`);
        return;
      }

      // Check for duplicates
      const isDuplicate = files?.some(existingFile => 
        existingFile?.name === file?.name && existingFile?.size === file?.size
      );

      if (isDuplicate) {
        errors?.push(`${file?.name} is already uploaded`);
        return;
      }

      validFiles?.push({
        file,
        id: Date.now() + index,
        name: file?.name,
        size: file?.size,
        type: file?.type,
        uploadProgress: 0
      });
    });

    setUploadErrors(errors);

    if (validFiles?.length > 0) {
      onFilesChange([...files, ...validFiles]);
    }
  };

  const removeFile = (fileId) => {
    const updatedFiles = files?.filter(file => file?.id !== fileId);
    onFilesChange(updatedFiles);
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i))?.toFixed(2)) + ' ' + sizes?.[i];
  };

  const getFileIcon = (fileType) => {
    if (fileType?.includes('pdf')) return 'FileText';
    if (fileType?.includes('word') || fileType?.includes('document')) return 'FileText';
    if (fileType?.includes('excel') || fileType?.includes('sheet')) return 'FileSpreadsheet';
    if (fileType?.includes('image')) return 'Image';
    return 'File';
  };

  return (
    <div className="space-y-6">
      <div className="text-center">
        <h2 className="text-2xl font-semibold text-foreground mb-2">Supporting Documents</h2>
        <p className="text-muted-foreground">
          Upload any relevant documents, diagrams, or specifications (Optional)
        </p>
      </div>
      <div className="bg-card border border-border rounded-lg p-6">
        {/* Upload Area */}
        <div
          className={`relative border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
            dragActive
              ? 'border-primary bg-primary/5' :'border-border hover:border-primary/50'
          }`}
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
        >
          <input
            type="file"
            multiple
            onChange={handleChange}
            accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png,.gif,.txt"
            className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
          />
          
          <div className="space-y-4">
            <div className="mx-auto w-12 h-12 bg-muted rounded-lg flex items-center justify-center">
              <Icon name="Upload" size={24} className="text-muted-foreground" />
            </div>
            
            <div>
              <p className="text-lg font-medium text-foreground mb-2">
                Drop files here or click to browse
              </p>
              <p className="text-sm text-muted-foreground">
                Supported formats: PDF, DOC, DOCX, XLS, XLSX, JPG, PNG, GIF, TXT
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                Maximum {maxFiles} files, {maxSizePerFile}MB per file
              </p>
            </div>
          </div>
        </div>

        {/* Upload Errors */}
        {uploadErrors?.length > 0 && (
          <div className="mt-4 p-4 bg-error/10 border border-error/20 rounded-lg">
            <div className="flex items-start space-x-2">
              <Icon name="AlertCircle" size={16} className="text-error mt-0.5" />
              <div>
                <p className="text-sm font-medium text-error mb-1">Upload Errors:</p>
                <ul className="text-sm text-error space-y-1">
                  {uploadErrors?.map((error, index) => (
                    <li key={index}>• {error}</li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        )}

        {/* File List */}
        {files?.length > 0 && (
          <div className="mt-6">
            <h4 className="text-sm font-medium text-foreground mb-4">
              Uploaded Files ({files?.length}/{maxFiles})
            </h4>
            
            <div className="space-y-3">
              {files?.map((fileItem) => (
                <div
                  key={fileItem?.id}
                  className="flex items-center justify-between p-3 bg-muted rounded-lg"
                >
                  <div className="flex items-center space-x-3">
                    <div className="p-2 bg-background rounded">
                      <Icon name={getFileIcon(fileItem?.type)} size={16} className="text-muted-foreground" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-foreground">{fileItem?.name}</p>
                      <p className="text-xs text-muted-foreground">
                        {formatFileSize(fileItem?.size)}
                      </p>
                    </div>
                  </div>
                  
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => removeFile(fileItem?.id)}
                    className="text-error hover:text-error hover:bg-error/10"
                  >
                    <Icon name="X" size={16} />
                  </Button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* File Type Guidelines */}
        <div className="mt-6 p-4 bg-muted/50 rounded-lg">
          <h4 className="text-sm font-medium text-foreground mb-3">Helpful Documents to Include:</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-muted-foreground">
            <div>
              <p className="font-medium text-foreground mb-1">Technical Documents:</p>
              <ul className="space-y-1">
                <li>• Network diagrams</li>
                <li>• System architecture</li>
                <li>• Technical specifications</li>
                <li>• Current system documentation</li>
              </ul>
            </div>
            <div>
              <p className="font-medium text-foreground mb-1">Business Documents:</p>
              <ul className="space-y-1">
                <li>• Requirements document</li>
                <li>• Process workflows</li>
                <li>• Compliance requirements</li>
                <li>• Reference materials</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FileUploadSection;